import React from 'react';
import { Col, Row, Typography, Collapse, Image } from 'antd';
import moment from 'moment';
import { DATE_FORMAT, DATE_FORMAT_PAYMENT, DATE_FORMAT_PICKUP } from 'common/const/common.const';
import { dateFormatter } from 'common/helpers/date.helper';
import { nameBuilder } from 'common/helpers/name.helper';
import { formatStringToShort } from 'common/helpers/format.helper';
import BitecoinIcon from 'app/assets/images/bitecoinIcon.png';
import { EStatisticStoreOrderStatus } from 'entities/Statistic/Statistic.models';
import { IOrderItemsModel, IOrderModel } from 'entities/Order/Order.models';
import { rejectReasonText } from 'entities/Order/order.helper';

interface IComponentProps {
  item: IOrderModel;
  showCreateDate?: boolean;
}
export const OrderListItem: React.FC<IComponentProps> = (props: IComponentProps) => {
  const { item, showCreateDate } = props;
  const {
    code,
    total,
    id,
    pickUpTime,
    customerData,
    items,
    status,
    rejectReason,
    createdAt,
    rejectDetails,
    discountTotal,
    checkoutSession,
  } = item;
  const customerName = nameBuilder(customerData?.firstName, customerData?.lastName);
  const customerPhone = customerData?.phone || 'Unknown number';
  const canceledByStore = status === EStatisticStoreOrderStatus.CanceledByStore;
  const canceledByCustomer = status === EStatisticStoreOrderStatus.CanceledByCustomer;
  const isPaymentAwaiting = status === EStatisticStoreOrderStatus.PaymentAwaiting;
  const isCanceled = canceledByStore || canceledByCustomer;
  const time = moment(pickUpTime).format(DATE_FORMAT_PICKUP);
  const createDate = dateFormatter(createdAt, DATE_FORMAT);
  const canceledType = canceledByStore
    ? 'Canceled by store'
    : canceledByCustomer
    ? 'Canceled by customer'
    : 'Unknown canceled type';
  const paymentSessionId = checkoutSession
    ? `Payment session ID: ${formatStringToShort(checkoutSession.id)} - ${dateFormatter(
        checkoutSession.createdAt,
        DATE_FORMAT_PAYMENT
      )}`
    : '';
  const spentBitecoins = items.reduce((acc, curr) => (acc += curr.totalInBitecoins || 0), 0);

  const renderOrderItems = (items: IOrderItemsModel[]) =>
    items.map((item) => {
      const { quantity, assetItemData, addOns, mealBuilderPicks, id } = item;
      const itemName = assetItemData.name;

      const renderChild = (child: IOrderItemsModel['addOns'][number]) => (
        <React.Fragment key={child.id}>
          <span
            className="franchisee__text_default font-weight-4"
            title={`${child.quantity} x ${child.assetItemData.name}`}
          >
            {child.quantity} x {child.assetItemData.name}
          </span>
          <br />
        </React.Fragment>
      );

      return (
        <React.Fragment key={id}>
          <br />
          <span className="franchisee__text_default font-weight-7" title={`${quantity} x ${itemName}`}>
            {quantity} x {itemName}
          </span>
          <br />
          {addOns?.map(renderChild)}
          {mealBuilderPicks?.map(renderChild)}
        </React.Fragment>
      );
    });

  const rejectReasonValue = rejectReasonText(rejectReason, rejectDetails);

  return (
    <Collapse>
      <Collapse.Panel
        header={
          <Row justify="space-between" align="middle">
            <Col>
              <Row justify="start" className="flex-noWrap">
                <Typography.Paragraph
                  className="m-0 p-0 franchisee__text_paragraph"
                  ellipsis={{ rows: 1 }}
                  title={code}
                >
                  {code}
                </Typography.Paragraph>
              </Row>
              <Row justify="start" className="flex-noWrap">
                <span className="franchisee__text_paragraph white-space-nowrap" title={time}>
                  {time}
                </span>
              </Row>
              {showCreateDate && (
                <Row>
                  <span className="franchisee__text_paragraph mr-2" title={createDate}>
                    {createDate}
                  </span>
                </Row>
              )}
            </Col>
            <Col>
              <Row justify="end" className="flex-noWrap">
                <span className="franchisee__text_paragraph mr-2" title="TOTAL:">
                  TOTAL:
                </span>
                <Typography.Paragraph
                  className="m-0 p-0 franchisee__text_paragraph"
                  ellipsis={{ rows: 1 }}
                  title={`${total}`}
                >
                  P{discountTotal || total}
                </Typography.Paragraph>
              </Row>
              {!!spentBitecoins && (
                <Row justify="end" gutter={4}>
                  <Col>
                    <Image src={BitecoinIcon} width={20} height={20} />
                  </Col>
                  <Col>
                    <span className="franchisee__text_paragraph">{spentBitecoins}</span>
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
        }
        key={id}
      >
        <span className="franchisee__text_default font-weight-4" title={`${customerName}: ${customerPhone}`}>
          {customerName}: {customerPhone}
        </span>
        <br />
        {isPaymentAwaiting && (
          <>
            <span className="franchisee__text_default font-weight-4" title={paymentSessionId}>
              {paymentSessionId}
            </span>
            <br />
          </>
        )}
        {renderOrderItems(items)}
        {isCanceled && (
          <>
            <br />
            <span className="franchisee__text_default font-weight-7" title={canceledType}>
              {canceledType}:
            </span>
            <br />
            <span className="franchisee__text_default font-weight-4" title={rejectReasonValue}>
              {rejectReasonValue}
            </span>
          </>
        )}
      </Collapse.Panel>
    </Collapse>
  );
};
